import React from 'react';
import { Chip } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Cross-reference code pattern: F001, US001, PF001 (3-4 digits)
 */
const CROSS_REF_REGEX = /\b(F\d{3,4}|US\d{3,4}|PF\d{3,4})\b/g;

/**
 * Map code prefix to task_id for navigation.
 */
const CODE_TO_TASK = {
  F: 'features',
  US: 'user-stories',
  PF: 'process-flows',
};

const getTaskForCode = (code) => {
  const prefix = code.replace(/\d+/g, '');
  return CODE_TO_TASK[prefix] || null;
};

/**
 * CrossRefLink — Renders a single clickable cross-reference chip.
 */
const CrossRefLink = ({ code, projectId }) => {
  const navigate = useNavigate();
  const task = getTaskForCode(code);

  if (!task || !projectId) {
    return <span>{code}</span>;
  }

  const handleClick = (e) => {
    e.stopPropagation();
    const sectionId = code.toLowerCase();
    navigate(`/projects/${projectId}/conversation?task=${task}&section=${sectionId}`);
  };

  const colors = {
    F: { color: '#7c3aed', bg: '#f3e8ff' },
    US: { color: '#0891b2', bg: '#e0f7fa' },
    PF: { color: '#d97706', bg: '#fef3c7' },
  };
  const prefix = code.replace(/\d+/g, '');
  const { color, bg } = colors[prefix] || { color: '#475969', bg: '#f5f5f5' };

  return (
    <Chip
      label={code}
      size="small"
      onClick={handleClick}
      sx={{
        height: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
        bgcolor: bg, color, border: 'none',
        mx: 0.25, verticalAlign: 'middle',
        '&:hover': { opacity: 0.8, textDecoration: 'underline' },
      }}
    />
  );
};

/**
 * linkifyCrossRefs — Processes a text string and replaces F001/US001/PF001
 * patterns with clickable CrossRefLink components.
 *
 * @param {string} text - Plain text that may contain cross-reference codes
 * @param {string} projectId - Project ID for navigation
 * @returns {React.ReactNode[]} Array of text and CrossRefLink elements
 */
const linkifyCrossRefs = (text, projectId) => {
  if (!text || typeof text !== 'string' || !projectId) return text;

  const parts = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(CROSS_REF_REGEX.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <CrossRefLink key={`${match[1]}-${match.index}`} code={match[1]} projectId={projectId} />
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

/**
 * CrossRefText — Component wrapper for linkifyCrossRefs.
 * Use this in places where you render plain text that might have cross-refs.
 */
const CrossRefText = ({ children, projectId }) => {
  const params = useParams();
  const pid = projectId || params.projectId;

  if (typeof children !== 'string') return children;

  const linked = linkifyCrossRefs(children, pid);
  return <>{linked}</>;
};

export { CrossRefLink, linkifyCrossRefs, CROSS_REF_REGEX, getTaskForCode };
export default CrossRefText;
